import { useMemo } from 'react';
import { useStytch as originalUseStytch } from '@stytch/react';
import {
  PasswordResetByExistingPasswordOptions,
  StytchSDKAPIError,
} from '@stytch/vanilla-js';

import { sendIncrementMetric } from 'util/dataDogMetrics';

const DEFAULT_SESSION_DURATION_MINUTES = 20_160; // 2 weeks
export const OTP_EXPIRATION_MINUTES = 5;

const OTP_AUTHENTICATE = 'otp_authenticate';
const PASSWORD_AUTHENTICATE = 'password_authenticate';
const MFA_OTP_SMS_SEND = 'mfa_otp_sms_send';
const RESET_PASSWORD_BY_SESSION = 'reset_password_by_session';
const RESET_PASSWORD_BY_EXISTING_PASSWORD =
  'reset_password_by_existing_password';

const trackSuccess = <T>(message: string, response: T): T => {
  sendIncrementMetric({
    metricName: `stytch.${message}`,
    tags: [`status:success`],
  });

  return response;
};

const trackError = (message: string, error: StytchSDKAPIError) => {
  sendIncrementMetric({
    metricName: `stytch.${message}`,
    tags: ['status:error', `error_type:${error.error_type}`],
  });

  throw error;
};

const selectLanguage = () => {
  const language = window.Homebase.currentUser.language;
  return language === 'es' ? 'es' : 'en';
};

export const useStytch = () => {
  const stytchClient = originalUseStytch();
  const sessionDurationMinutes = DEFAULT_SESSION_DURATION_MINUTES;

  return useMemo(
    () => ({
      authenticateOtp: (code: string, methodId: string) =>
        stytchClient.otps
          .authenticate(code, methodId, {
            session_duration_minutes: sessionDurationMinutes,
          })
          .then(res => trackSuccess(OTP_AUTHENTICATE, res))
          .catch(error => trackError(OTP_AUTHENTICATE, error)),
      authenticatePassword: (email: string, password: string) =>
        stytchClient.passwords
          .authenticate({
            email,
            password,
            session_duration_minutes: sessionDurationMinutes,
          })
          .then(res => trackSuccess(PASSWORD_AUTHENTICATE, res))
          .catch(error => trackError(PASSWORD_AUTHENTICATE, error)),
      // Not used for phone sign in - only for MFA login
      sendMfaSmsOtp: (phone: string) =>
        stytchClient.otps.sms
          .send(phone, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
            locale: selectLanguage(),
          })
          .then(res => trackSuccess(MFA_OTP_SMS_SEND, res))
          .catch(error => trackError(MFA_OTP_SMS_SEND, error)),
      resetPasswordBySession: (newPassword: string) =>
        stytchClient.passwords
          .resetBySession({
            password: newPassword,
            session_duration_minutes: sessionDurationMinutes,
          })
          .then(res => trackSuccess(RESET_PASSWORD_BY_SESSION, res))
          .catch(error => trackError(RESET_PASSWORD_BY_SESSION, error)),
      resetPasswordByExistingPassword: (
        values: PasswordResetByExistingPasswordOptions
      ) =>
        stytchClient.passwords
          .resetByExistingPassword({
            ...values,
            session_duration_minutes: sessionDurationMinutes,
          })
          .then(async ({ session_token, session_jwt }) => {
            // TODO: Will assess if we need this after updating Stytch libs in FE-2005
            stytchClient.session.updateSession({ session_token, session_jwt });

            try {
              const res = await stytchClient.session.authenticate({
                session_duration_minutes: sessionDurationMinutes,
              });
              return trackSuccess(RESET_PASSWORD_BY_EXISTING_PASSWORD, res);
            } catch (error) {
              return trackError(
                RESET_PASSWORD_BY_EXISTING_PASSWORD,
                error as StytchSDKAPIError
              );
            }
          })
          .catch(error =>
            trackError(RESET_PASSWORD_BY_EXISTING_PASSWORD, error)
          ),
    }),
    [sessionDurationMinutes, stytchClient]
  );
};
